import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
// import { environment } from "@environment";
import { ConfigService } from "../../config.service";
import { BehaviorSubject, Observable, Subject, interval, throwError } from "rxjs";
import { filter, map, startWith, takeUntil, tap } from "rxjs/operators";
import { Utilities } from "src/app/_helpers/utility/utilities";
import { User } from "src/app/_models";
import { UserRoleType } from "src/app/_models/user/userRoleType";
import { DBkeys } from "./dbkeys.service";
import { LocalStoreManagerService } from "./local-store-manager.service";
import { EInterceptorOptionsHTTPHeaders } from "src/app/_helpers/interceptors/EInterceptorOptionsHTTPHeaders";
import { UserDetailsService } from "../user-details.service";
import { GeographicAreasService } from "../../shared/geographic-areas/geographic-areas.service";
import { IIdToken } from "src/app/_interfaces/auth/IIdToken";
import { IWFDSSUserRoleType } from "src/app/_interfaces/auth/IWfdssUserRoleType";
import { DialogService } from "../../shared/dialog.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService implements OnDestroy {
  public get loginUrl() {
    return "";
  }

  public get homeUrl() {
    return "";
  }
  private _currentUser: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public currentUser$ = this._currentUser.asObservable().pipe(
    filter((user: User)=> {
      if(user){return true}
      return false
    })
  )
  public loginRedirectUrl: string;
  public logoutRedirectUrl: string;

  public reLoginDelegate: () => void;

  private previousIsLoggedInCheck = false;
  private _loginStatus = new Subject<boolean>();

  public bearerToken?: string;
  public tokenRefreshInProgress: boolean = false;
  public userManualLoginInProgress: boolean = false;
  private loginStatusPollingStarted = false
  // private _loggedInUserIsElevated = new BehaviorSubject<boolean>(false)
  public loggedInUserIsElevated$: Observable<boolean>
  public userIsLoggedIn$: Observable<boolean>

  private _destroying = new Subject<void>()
  private destroying$ = this._destroying.asObservable()

  constructor(
    private router: Router,
    private localStorage: LocalStoreManagerService,
    private httpClient: HttpClient,
    private userService: UserDetailsService,
    private environment: ConfigService,
    private dialogService: DialogService
  ) {
    const allowedRoles = [
        "SUPPORT",
        "GA_EDITOR",
        "INCIDENT_EDITOR",
        "ANALYST",
      ];
    this.loggedInUserIsElevated$ = this._currentUser.pipe(
      map((user)=>{
        if(!user){
          return false
        }
        const roles = user.role;
        return allowedRoles.some((role) => role === roles.roleType);
      })
    )
  }

  /**
   * @description Starts the polling of the login status. Every 5 seconds, the token is checked for expiry. Must be consumed only in the ngOnInit hook of app component.
   * @returns Observable with void value. Must be subscribed to exactly once at root(app component)
   */
  public appComponentLoginStatusPolling(){
    if(this.loginStatusPollingStarted == true){
      throw Error("Auth Service: Login status polling has already been initiated")
    } 
    this.loginStatusPollingStarted = true
    return interval(5000).pipe(
      startWith(1),
      tap(()=>{
        this.reevaluateLoginStatus()
      })
    )
  }

  // To be deleted, use currentUser$ and UserHasRolePipe instead
  public loggedInUserIsNotViewer() {
    if (this.isLoggedIn) {
      const roles = this.currentUser.role;
      const allowedRoles = [
        "SUPPORT",
        "GA_EDITOR",
        "INCIDENT_EDITOR",
        "ANALYST",
      ];

      return allowedRoles.some((role) => role === roles.roleType);
      // return roles.some(role => {
      //   return !!allowedRoles.find(allowedRole => role.roleType === allowedRole);
      // });
    } else {
      return false;
    }
  }
  // To be deleted, use currentUser$ and UserHasRolePipe instead
  public loggedInUserIsElevated() {
    //break out to take in role as a parameter
    if (this.isLoggedIn) {
      const roles = this.currentUser.role;
      const allowedRoles = ["SUPPORT", "ANALYST", "GA_EDITOR"];

      return allowedRoles.some((role) => roles.roleType === role);

      // return roles.some(role => {
      //   return !!allowedRoles.find(allowedRole => role.roleType === allowedRole);
      // });
    } else {
      return false;
    }
  }
  // To be deleted, use currentUser$ and UserHasRolePipe instead
  public loggedInUserHasRoles(rolesUserNeedsToBe: IWFDSSUserRoleType[]) {
    const roles = this.currentUser.role;
    return rolesUserNeedsToBe.some((role) => roles.roleType === role);
  }

  public exchangeAuthCodeForIdToken(code, requestedRole = "none") {
    return this.httpClient
      .post(
        this.environment.auth.idTokenUrl,
        { code },
        {
          params: {
            localRedirect: this.environment.auth.isLocal,
            ...(!this.environment.production ? { requestedRole } : {}),
          },
          headers: Utilities.appendHTTPHeaderForInterceptor([
            EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor,
          ]),
          observe: "response",
          responseType: "json",
        }
      )
      .pipe(
        map(async (resp: any) => {
          const idTokenString: string = resp.body?.id_token;
          const id_token: IIdToken = this.parseJwt(idTokenString) as IIdToken;
          this.bearerToken = idTokenString;

          const user = await this.userService
            .getUserByUserName(id_token.username, this.environment.userApi.baseUrl)
            .toPromise();

          this.processLoginResponse(
            user,
            new Date(id_token.exp * 1000),                                                
            idTokenString,
            resp.body?.refresh_token
          );

          console.debug(id_token, "Id token obtained");
          return {
            token_string: idTokenString,
            tokenObject: id_token,
            user,
          };
        })
      );
  }

  gotoPage(page: string, preserveParams = true) {
    let navigationExtras: NavigationExtras = {
      queryParamsHandling: preserveParams ? "merge" : "",
      preserveFragment: preserveParams,
    };

    this.router.navigate([page], navigationExtras);
  }

  redirectLogoutUser() {
    let redirect = this.logoutRedirectUrl
      ? this.logoutRedirectUrl
      : this.loginUrl;
    this.logoutRedirectUrl = null;

    this.router.navigate([redirect]);
  }

  redirectForLogin() {
    this.loginRedirectUrl = this.router.url;
    this.router.navigate([this.loginUrl]);
  }

  useRefreshToken() {
    // TODO: Create a function that lets users login via a
    // new tab so that their tokens are refreshed and they can continue working

    this.tokenRefreshInProgress = true;

    this.httpClient
      .post(this.environment.auth.refreshTokenUrl, {refresh_token: this.refreshToken},
        { // #6321: This is to avoid 401 error message popup when expired refresh token tries to fetch authentication JWT Token
          headers: Utilities.appendHTTPHeaderForInterceptor([
            EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor,
          ]),
        }
    )
      .subscribe(
        async (resp: any) => {
          const idTokenString: string = resp.id_token;
          const id_token: IIdToken = this.parseJwt(idTokenString) as IIdToken;
          this.bearerToken = idTokenString;

          const user = await this.userService
            .getUserByUserName(id_token.username, this.environment.userApi.baseUrl)
            .toPromise();

          this.processLoginResponse(
            user,
            new Date(id_token.exp * 1000),
            idTokenString,
            this.refreshToken
          );

          console.debug(id_token, "Id token obtained via Refresh");
          this.tokenRefreshInProgress = false;
        },
        (error) => {
          this.tokenRefreshInProgress = false;
          // Redirect users to relogin here as well
          // #6321: Redirect to login is not needed here as appComponentLoginStatusPolling function (polling after every 5 seconds) takes care of redirecting user to login page after token is expired
          console.error(error);
        }
      );
  }


  getUserName() {
    return this.localStorage.getDataObject<User>("USERNAME_TEMP");
  }

  getUserPassword() {
    return this.localStorage.getDataObject<User>("PASSWORD_TEMP");
  }

  saveUserNamePassword(userName, password) {
    //TODO:Change this when we are no longer using user/pass auth
    this.localStorage.savePermanentData(userName, "USERNAME_TEMP");
    this.localStorage.savePermanentData(password, "PASSWORD_TEMP");
  }

  doesUserHaveRole(userRole: UserRoleType): boolean {
    // console.log("requested role to check", userRole, "current user role", this.currentUser.role.roleType)
    if (userRole === this.currentUser?.role?.roleType) {
      return true;
    }
    return false;
  }

  parseJwt(unparsedJwt: string) {
    let jwt = {};
    try {
      jwt = JSON.parse(atob(unparsedJwt.split(".")[1]));
    } catch (ex) {
      console.error("Failed to parse JWT");
    }
    return jwt;
  }

  public processLoginResponse(
    userResponse: any,
    expiration: Date,
    idTokenString: string,
    refreshTokenString: string
  ) {
    let user = new User(userResponse);

    this.saveUserDetails(user, idTokenString, expiration, refreshTokenString);

    return user;
  }

  logout(): void {
    this.removeUserData();
    window.location.href = "/";
  }

  removeUserData() {
    this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
    this.localStorage.deleteData(DBkeys.ID_TOKEN);
    this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);    
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);     
    this.localStorage.deleteData(DBkeys.USER_PERMISSIONS);
    this.localStorage.deleteData(DBkeys.CURRENT_USER);
  }

  private reevaluateLoginStatus() {
    if (
      this.tokenRefreshInProgress ||
      window.location.pathname === "/auth/sso" ||
      window.location.pathname === "/auth/validate"
    ) {
      return;
    }

    const expiryString = this.localStorage.getDataObject<Date>(
      DBkeys.TOKEN_EXPIRES_IN
    );
    const expiry = new Date(expiryString);
    this.bearerToken = this.localStorage.getData(DBkeys.ID_TOKEN);

    // console.debug("Revealuating login status for expiry: ", expiry);

    let expired = expiryString && new Date() >= expiry;
    if (expired) {
      if (!this.userManualLoginInProgress) {
        this.removeUserData();
        this.dialogService.openConfirmDialog('Your session has expired. Would you like to login again?', 'Yes', 'No', true)
        .afterClosed()
        .pipe(takeUntil(this.destroying$))
        .subscribe((decision) => {
          if (decision === true) {      
            this.dialogService.openAlertDialog('Please complete login in new WFDSS window, then continue.')
            this.openLoginInNewTab();
          }
        });
      }
      this.userManualLoginInProgress = true;     

      // if (this.userManualLoginInProgress) {
      //   alert("Please complete login in new WFDSS window, then continue.");
      // }
      // this.userManualLoginInProgress = window.confirm(
      //   "Your session has expired. Would you like to login again?"
      // );
      // if (this.userManualLoginInProgress) {
      //   this.openLoginInNewTab();
      //   return;
      // }

      // #6321: If expired, to preserve user data, we must avoid logging out (just remove the user data instead)
      // this.logout();
      // #6321: 'Removing user data' was logging user out of the session in the original tab. So, had to comment this out.
      // this.removeUserData();
      // alert("Session expired. Please login again");
    } else {
      // After user logs back in, userManualLoginInProgress is set true (since it happens every 5 seconds)
      this.userManualLoginInProgress = false;
    }

    // If user was previously logged in, and id token isn't expired
    if (!expired && this.previousIsLoggedInCheck) {
      this.userManualLoginInProgress = false;
      const twoMinutesToExpiry = this.getTwoMinutesToExpiry(expiry);
      if (new Date() >= twoMinutesToExpiry) {
        this.useRefreshToken();
      }
    }

    // Finally update whether a user is logged in or logged out:
    let isLoggedIn = this.localStorage.getData(DBkeys.ID_TOKEN) && !expired;

    if (this.previousIsLoggedInCheck !== isLoggedIn) {
      setTimeout(() => {
        this._loginStatus.next(isLoggedIn);
        this._currentUser.next(this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER))
      });
    }
    this.previousIsLoggedInCheck = isLoggedIn;
  }

  getTwoMinutesToExpiry(expiry: Date) {
    const expTimestamp = +expiry;
    const twoMinuteSubtraction = 1000 * 60 * 2;
    const finalDate = new Date(expTimestamp - twoMinuteSubtraction);
    return finalDate;
  }

  private saveUserDetails(
    user: User,
    idToken: string,
    expiresIn: Date,
    refreshToken: string
  ) {
    this.localStorage.savePermanentData(idToken, DBkeys.ID_TOKEN);
    this.localStorage.savePermanentData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
    this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
    this.localStorage.savePermanentData(refreshToken, DBkeys.REFRESH_TOKEN);
  }

  set currentUser(value) {
    this.localStorage.saveSyncedSessionData(value, DBkeys.CURRENT_USER);
  }

  get currentUser(): User {
    let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    return user;
  }

  getLoginStatusEvent(): Observable<boolean> {
    return this._loginStatus.asObservable();
  }

  openLoginInNewTab() {
    const link = document.createElement("a");
    link.setAttribute("href", this.environment.auth.authFlowInitiationUrl);
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
    link.click();
  }

  get accessTokenExpiryDate(): Date {
    return this.localStorage.getDataObject<Date>(DBkeys.TOKEN_EXPIRES_IN, true);
  }

  get isSessionExpired(): boolean {
    if (this.accessTokenExpiryDate == null) {
      return true;
    }

    return !(this.accessTokenExpiryDate.valueOf() > new Date().valueOf());
  }

  get idToken(): string {
    return this.localStorage.getData(DBkeys.ID_TOKEN);
  }

  get refreshToken(): string {
    return this.localStorage.getData(DBkeys.REFRESH_TOKEN);
  }

  get isLoggedIn(): boolean {
    return this.currentUser != null;
  }

  get rememberMe(): boolean {
    return this.localStorage.getDataObject<boolean>(DBkeys.REMEMBER_ME) == true;
  }

  ngOnDestroy(): void {
    this._destroying.unsubscribe()
    this._destroying.complete()
  }
}